import React, { useRef, useEffect, useState, ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

interface ZoomableDivWrapperProps {
  children: ReactNode
  initialScale?: number
  minScale?: number
  maxScale?: number
  isLoading?: boolean
}

const ZoomableTableWrapper: React.FC<ZoomableDivWrapperProps> = ({
  children,
  initialScale = 1,
  minScale = 0.5,
  maxScale = 3,
  isLoading = false,
}) => {
  const { t } = useTranslation('pondOverview')
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(initialScale)
  const scaleRef = useRef(scale)
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor
    const ios =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    setIsIOS(ios)
  }, [])

  useEffect(() => {
    scaleRef.current = scale
  }, [scale])

  const clamp = (v: number) => Math.min(maxScale, Math.max(minScale, v))

  const onWheel = useCallback((e: WheelEvent) => {
    if (!e.ctrlKey) return
    e.preventDefault()

    const delta = -e.deltaY * 0.01
    setScale((s) => clamp(s * (1 + delta)))
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('wheel', onWheel, { passive: false })

    let startDist = 0
    let startScale = scaleRef.current

    const distance = (t: TouchList) => {
      const [a, b] = [t[0], t[1]]
      return Math.hypot(a.pageX - b.pageX, a.pageY - b.pageY)
    }

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        startDist = distance(e.touches)
        startScale = scaleRef.current
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        const factor = distance(e.touches) / (startDist || 1)
        setScale(clamp(startScale * factor))
      }
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        startDist = 0
      }
    }

    container.addEventListener('touchstart', onTouchStart, { passive: false })
    container.addEventListener('touchmove', onTouchMove, { passive: false })
    container.addEventListener('touchend', onTouchEnd, { passive: false })

    return () => {
      container.removeEventListener('wheel', onWheel as any)
      container.removeEventListener('touchstart', onTouchStart as any)
      container.removeEventListener('touchmove', onTouchMove as any)
      container.removeEventListener('touchend', onTouchEnd as any)
    }
  }, [onWheel])

  // Per-platform scaling mechanism (NK-198):
  // - iOS WebKit ignores CSS `zoom` on position:sticky descendants, so the
  //   frozen header/columns render un-zoomed under `zoom`. There we MUST scale
  //   with `transform`, which scales the whole painted subtree (sticky included)
  //   as one unit; the content is sized 1/scale and origin-anchored so it fills
  //   the viewport exactly after scaling.
  // - On Android WebView (Blink) and desktop, `transform: scale()` desyncs
  //   @tanstack/react-virtual's container measurement from the rows' translateY
  //   offsets, so rows and pinned columns overlap ("เละ") when zooming. CSS
  //   `zoom` affects layout and keeps the scroller + virtualizer in one
  //   coordinate space — the proven-good path before the iOS-only transform
  //   switch — so we use it on every non-iOS engine.
  const scaledContentStyle: React.CSSProperties = isIOS
    ? {
        transform: `scale(${scale})`,
        transformOrigin: '0 0',
        width: `${100 / scale}%`,
        height: `${100 / scale}%`,
        // The wide table can trigger iOS text autosizing; disable it so the
        // transform is the only thing scaling the text.
        WebkitTextSizeAdjust: 'none',
        textSizeAdjust: 'none',
      }
    : {
        zoom: scale,
        width: '100%',
        height: '100%',
      }

  const overlayScaleStyle: React.CSSProperties = isIOS
    ? { transform: `scale(${1 / scale})`, transformOrigin: '0 0' }
    : { zoom: 1 / scale }

  // No bottom padding: under the global border-box it came out of this 100%
  // height, so the table stopped 30–40px short of the WebView's bottom edge and
  // left a blank band above the app's navbar. Scrolling the last rows clear of
  // the keyboard is the spacer inside the scaled div's job, not this box's.
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          // iOS: clip the 1/scale-oversized transform child (its own inner
          // scroller handles scrolling, avoiding phantom outer scrollbars).
          // Non-iOS uses CSS `zoom` where the child is sized 100% (not
          // oversized), so `auto` matches the proven pre-regression behavior.
          overflow: isIOS ? 'hidden' : 'auto',
          position: 'relative',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: 'transparent',
          touchAction: 'pan-x pan-y',
          ...(isIOS
            ? {
                overscrollBehavior: 'none' as const,
                overscrollBehaviorX: 'none' as const,
                overscrollBehaviorY: 'none' as const,
              }
            : {}),
        }}
      >
        <div style={scaledContentStyle}>
          {isLoading && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                zIndex: 2000,
                ...overlayScaleStyle,
              }}
            >
              <div
                style={{
                  background: 'white',
                  padding: '10px 20px',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.3)',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                <div
                  style={{
                    marginTop: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#424242',
                  }}
                >
                  {t('loading')}
                </div>
              </div>
            </div>
          )}

          {children}

          {/* Extra space so bottom rows can be scrolled above the keyboard.
              Must live inside the scaled div so it extends past the table
              content. CSS height is divided by scale so the rendered visual
              height is always ~350 px regardless of the current zoom level. */}
          {isIOS && <div style={{ height: 350 / scale }} aria-hidden="true" />}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

export default ZoomableTableWrapper
